// ---------------------------------------------------------------------------
// SWEEP S21. CHANGING A REQUIREMENT AFTER IT EXISTS.
//
// `PATCH /v1/requirements/:id` took the shift, the post, the headcount and the break
// from the first day, and the only caller pinned charge rates. A requirement typed
// with the wrong start had to be removed and typed again, which a booked one cannot
// be. The role is not here: the database refuses to rewrite what was demanded (0019),
// and the server's sentence says so if anyone tries.
//
// Only what changed is sent, so an edit to the headcount never re-sends a shift that
// the server would re-validate against bookings it has nothing to do with.
// ---------------------------------------------------------------------------

import { useState } from "react";

import { send } from "../api/auth";
import { console_, type Requirement } from "../api/console";
import { useSession } from "../session/SessionProvider";
import { typedTimeHint } from "../system/clock";
import { useT } from "../i18n/LocaleProvider";

/** An instant as the value a `datetime-local` input shows in this browser. */
function local(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function RequirementEditor({
  requirement: r,
  onSaved,
  onClose,
}: {
  requirement: Requirement;
  onSaved: () => void;
  onClose: () => void;
}) {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [zone, setZone] = useState(r.zone ?? "");
  const [headcount, setHeadcount] = useState(String(r.headcount));
  const [startsAt, setStartsAt] = useState(local(r.starts_at));
  const [endsAt, setEndsAt] = useState(local(r.ends_at));
  const [breakMinutes, setBreakMinutes] = useState(String(r.break_minutes));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changes: Record<string, unknown> = {};
  if (zone.trim() !== (r.zone ?? "")) changes.zone = zone.trim();
  if (headcount !== String(r.headcount)) changes.headcount = Number(headcount);
  if (startsAt !== local(r.starts_at)) changes.starts_at = new Date(startsAt).toISOString();
  if (endsAt !== local(r.ends_at)) changes.ends_at = new Date(endsAt).toISOString();
  if (breakMinutes !== String(r.break_minutes)) changes.break_minutes = Number(breakMinutes);
  const dirty = Object.keys(changes).length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!dirty) return;
        setBusy(true);
        setError(null);
        void send<{ error?: { message?: string } }>(console_.updateRequirement(apiBase, r.id, changes))
          .then(({ status, body }) => {
            if (status === 200) onSaved();
            else setError(refusal(body.error) ?? t("That change could not be saved."));
          })
          .catch(() => setError(t("We could not reach Verbunk. Check your connection and try again.")))
          .finally(() => setBusy(false));
      }}
    >
      <h3>{t("Change this requirement")}</h3>
      {error ? <p role="alert">{error}</p> : null}
      <div className="row">
        <div className="field">
          <label htmlFor={`edit-zone-${r.id}`}>{t("Post")}</label>
          <input id={`edit-zone-${r.id}`} maxLength={80} value={zone} onChange={(e) => setZone(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`edit-headcount-${r.id}`}>{t("People")}</label>
          <input
            id={`edit-headcount-${r.id}`}
            type="number"
            min={1}
            max={10000}
            required
            value={headcount}
            onChange={(e) => setHeadcount(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`edit-break-${r.id}`}>{t("Break, minutes")}</label>
          <input
            id={`edit-break-${r.id}`}
            type="number"
            min={0}
            max={1439}
            required
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(e.target.value)}
          />
        </div>
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor={`edit-starts-${r.id}`}>{t("Starts")}</label>
          <input id={`edit-starts-${r.id}`} type="datetime-local" required value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`edit-ends-${r.id}`}>{t("Ends")}</label>
          <input id={`edit-ends-${r.id}`} type="datetime-local" required value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </div>
      </div>
      <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
      <div className="row">
        <button className="btn" type="submit" disabled={busy || !dirty}>
          {busy ? t("Working…") : t("Save the changes")}
        </button>
        <button className="btn btn-quiet" type="button" onClick={onClose}>
          {t("Cancel")}
        </button>
      </div>
    </form>
  );
}
